import { useDispatch, useSelector } from 'react-redux'
import { useState, useEffect } from 'react'
import { Button, SelectField, toaster, Checkbox, TextInputField, Dialog } from 'evergreen-ui'
import DatePicker from 'react-datepicker'
import {
    format,
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    startOfYear,
    endOfYear
} from 'date-fns'
import { pl } from 'date-fns/locale'

import TopBar from '../../components/TopBar/TopBar'
import LeftMenu from '../../components/LeftMenu'
import Footer from '../../components/Footer'
import useApi from '../../hooks/useApi'
import { fromMeterApi } from '../../utils/Adapters/MeterApiAdapter'
import { toMeasurementReportApi } from '../../utils/Adapters/ReportApiAdapter'
import { addMeters } from '../../redux/slices/meterSlice'
import { addUsers } from '../../redux/slices/userSlice'
import fromGetUserTenantApi from '../../utils/Adapters/UserApiAdapter'
import getPathArguments from '../../utils/pathIDs'
import {
    setSelectedMeterIds,
    setDateRange,
    setCustomDateRange,
    setResolution,
    setExporting,
    setExportError,
    setExportSuccess,
    resetReportState
} from '../../redux/slices/reportsSlice'
import useTenantType from '../../hooks/useTenantType'
import renderMeterIconType from '../../utils/renderMeterIconType'

import '../../styles/views/reports.scss'
import '../../styles/components/table.scss'

const ReportsModerator = () => {
    const dispatch = useDispatch()

    const apiKey = useSelector((state) => state.profile.profile.apiKey)
    const meters = useSelector((state) => state.meter.meters)
    const users = useSelector((state) => state.user.users)
    const tenants = useSelector((state) => state.tenant.tenants)
    const {
        selectedMeterIds,
        dateFrom,
        dateTo,
        resolution,
        dateRange,
        isExporting,
        exportError,
        lastExportSuccess
    } = useSelector((state) => state.reports)

    const [isCustomDateRange, setIsCustomDateRange] = useState(false)
    const [customDateFrom, setCustomDateFrom] = useState(new Date())
    const [customDateTo, setCustomDateTo] = useState(new Date())
    const [isMeterSelectDialogOpen, setIsMeterSelectDialogOpen] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [meterPageCount, setMeterPageCount] = useState(1)
    const [meterOffset, setMeterOffset] = useState(0)

    const { isInstitutionalMonitoring } = useTenantType()

    // Date range options
    const dateRangeOptions = [
        { label: 'Dzień', value: 'day' },
        { label: 'Tydzień', value: 'week' },
        { label: 'Miesiąc', value: 'month' },
        { label: 'Rok', value: 'year' },
        { label: 'Od początku', value: 'from_beginning' },
        { label: 'Zakres niestandardowy', value: 'custom' }
    ]

    // Resolution options
    const resolutionOptions = [
        { label: '1 godzina', value: 'hour' },
        { label: '10 minut', value: '10min' }
    ]

    // Calculate date range based on selection
    const calculateDateRange = (range) => {
        const now = new Date()
        let from
        let to

        switch (range) {
            case 'day':
                from = startOfDay(now)
                to = endOfDay(now)
                break
            case 'week':
                from = startOfWeek(now, { locale: pl })
                to = endOfWeek(now, { locale: pl })
                break
            case 'month':
                from = startOfMonth(now)
                to = endOfMonth(now)
                break
            case 'year':
                from = startOfYear(now)
                to = endOfYear(now)
                break
            case 'from_beginning':
                from = new Date('2020-01-01') // Assuming service started in 2020
                to = endOfDay(now)
                break
            default:
                from = startOfDay(now)
                to = endOfDay(now)
        }

        return {
            dateFrom: format(from, 'yyyy-MM-dd HH:mm:ss'),
            dateTo: format(to, 'yyyy-MM-dd HH:mm:ss')
        }
    }

    // Build URL for meter fetching with pagination and search
    const buildMeterURL = () => {
        const apiLimit = 20
        let url = `/app/meters/moderator/meter/?limit=${apiLimit}&offset=${meterOffset}`

        if (searchText && searchText.length > 2) {
            url += `&search=${searchText}`
        }

        return url
    }

    // Fetch meters for selection with pagination
    const fetchMeters = useApi({
        url: buildMeterURL(),
        method: 'GET',
        apiKey,
        fromApiAdapter: fromMeterApi,
        queryName: ['metersForReports', meterOffset, searchText, isMeterSelectDialogOpen],
        enabled: isMeterSelectDialogOpen,
        onSuccess: (data) => {
            dispatch(addMeters(data))
        }
    })

    // Fetch users for the meters to display tenant/user information
    useApi({
        url: `/app/tenants/moderator/tenant_member/?id__in=${getPathArguments(
            meters,
            'tenantMember'
        )}`,
        method: 'GET',
        apiKey,
        enabled: fetchMeters.isSuccess && Object.keys(meters).length > 0,
        fromApiAdapter: fromGetUserTenantApi,
        queryName: ['usersForMeters', Object.keys(meters)],
        onSuccess: (data) => dispatch(addUsers(data))
    })

    // Export report API call
    const exportReport = useApi({
        method: 'GET',
        apiKey,
        toApiAdapter: toMeasurementReportApi
    })

    const handleDateRangeChange = (value) => {
        if (value === 'custom') {
            setIsCustomDateRange(true)
            return
        }

        setIsCustomDateRange(false)
        const dateRangeData = calculateDateRange(value)
        dispatch(
            setDateRange({
                range: value,
                dateFrom: dateRangeData.dateFrom,
                dateTo: dateRangeData.dateTo
            })
        )
    }

    const handleCustomDateChange = () => {
        dispatch(
            setCustomDateRange({
                dateFrom: format(customDateFrom, 'yyyy-MM-dd HH:mm:ss'),
                dateTo: format(customDateTo, 'yyyy-MM-dd HH:mm:ss')
            })
        )
        setIsCustomDateRange(false)
    }

    const handleMeterToggle = (meterId) => {
        const currentSelected = [...selectedMeterIds]
        const index = currentSelected.indexOf(meterId)

        if (index > -1) {
            currentSelected.splice(index, 1)
        } else {
            currentSelected.push(meterId)
        }

        dispatch(setSelectedMeterIds(currentSelected))
    }

    const handleExportClick = async () => {
        if (selectedMeterIds.length === 0) {
            toaster.danger('Wybierz co najmniej jeden licznik')
            return
        }

        if (!dateFrom || !dateTo) {
            toaster.danger('Wybierz zakres dat')
            return
        }

        dispatch(setExporting(true))

        try {
            // Build URL for the export API
            const baseUrl = '/app/meters/moderator/measurement/report/'
            const params = new URLSearchParams()

            selectedMeterIds.forEach((meterId) => {
                params.append('meter__in', meterId)
            })
            params.append('measured_ts_from', dateFrom)
            params.append('measured_ts_to', dateTo)
            params.append('resolution', resolution)

            const fullUrl = `${baseUrl}?${params.toString()}`

            const response = await fetch(`${process.env.REACT_APP_API_URL}${fullUrl}`, {
                method: 'GET',
                headers: {
                    Authorization: `Token ${apiKey}`,
                    'Content-Type': 'application/json'
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            // Handle JSON response
            const result = await response.json()

            if (result.status === 'ok') {
                dispatch(setExportSuccess())
                toaster.success(
                    'Generowanie raportu zostało rozpoczęte. Raport zostanie przygotowany i wysłany na adres e-mail.'
                )
            } else {
                throw new Error(result.message || 'Nieoczekiwana odpowiedź z serwera')
            }
        } catch (error) {
            dispatch(setExportError(error.message))
            toaster.danger(`Błąd podczas żądania raportu: ${error.message}`)
        }
    }

    const getFilteredMeters = () => {
        // Return meters as they come from API (already filtered by search on backend)
        return Object.values(meters).sort((a, b) => a.index - b.index)
    }

    const renderUserName = (meter) => {
        const user = users[meter.tenantMember]
        const isPrivate = meter.tenantMember === null

        if (isPrivate) {
            return '-'
        }
        if (user?.isCompany && user?.companyName === '' && user?.nameSurname === '') {
            return '-'
        }
        if (user?.isCompany && user?.companyName === '') {
            return user?.nameSurname
        }
        if (user?.isCompany) {
            return user?.companyName
        }
        return user?.nameSurname || '-'
    }

    const renderMeterLabel = (meter) => {
        const userDisplayName = renderUserName(meter)
        const meterTypeDisplay = renderMeterIconType(meter, 14, true)

        return (
            <div className="meter_label">
                <div className="meter_primary">
                    <span className="meter_name">{meter.name || 'Licznik'}</span>
                    <span className="meter_user"> • {userDisplayName}</span>
                </div>
                <div className="meter_secondary">
                    <span className="meter_type_icon">{meterTypeDisplay}</span>
                    {meter.serialNumber && (
                        <span className="meter_serial"> • Nr: {meter.serialNumber}</span>
                    )}
                    {meter.radioId && !isInstitutionalMonitoring && (
                        <span className="meter_radio"> • Radio: {meter.radioId}</span>
                    )}
                </div>
            </div>
        )
    }

    const handleMeterSearch = (text) => {
        setSearchText(text)
        setMeterOffset(0)
        setMeterPageCount(1)
    }

    const handleMeterPageClick = (direction) => {
        const apiLimit = 20
        const metersCount = fetchMeters.data?.count || 0
        const totalPages = Math.ceil(metersCount / apiLimit)

        if (direction === 1 && meterPageCount < totalPages) {
            setMeterPageCount((old) => old + 1)
            setMeterOffset((old) => old + apiLimit)
        } else if (direction === -1 && meterPageCount > 1) {
            setMeterPageCount((old) => old - 1)
            setMeterOffset((old) => old - apiLimit)
        }
    }

    const getSelectedMetersText = () => {
        if (selectedMeterIds.length === 0) {
            return isInstitutionalMonitoring ? 'Nie wybrano urządzeń' : 'Nie wybrano liczników'
        }
        if (selectedMeterIds.length === 1) {
            const selectedMeter = Object.values(meters).find((meter) =>
                selectedMeterIds.includes(meter.pk)
            )
            if (selectedMeter) {
                const userName = renderUserName(selectedMeter)
                return `${selectedMeter.name || 'Licznik'} (${userName})`
            }
            return isInstitutionalMonitoring ? '1 urządzenie wybrane' : '1 licznik wybrany'
        }
        const label = isInstitutionalMonitoring ? 'urządzeń' : 'liczników'
        return `${selectedMeterIds.length} ${label} wybranych`
    }

    const getDateRangeText = () => {
        if (!dateFrom || !dateTo) return 'Nie wybrano zakresu dat'

        const from = format(new Date(dateFrom), 'dd.MM.yyyy HH:mm')
        const to = format(new Date(dateTo), 'dd.MM.yyyy HH:mm')

        return `${from} - ${to}`
    }

    // Initialize with default date range
    useEffect(() => {
        if (!dateFrom || !dateTo) {
            handleDateRangeChange('day')
        }
    }, [])

    // Reset state when component unmounts
    useEffect(() => {
        return () => {
            dispatch(resetReportState())
        }
    }, [])

    return (
        <div className="main">
            <TopBar />
            <LeftMenu active="reports" />
            <div className="main_content">
                <div className="title">
                    <div className="title_name">Raporty</div>
                </div>

                <div className="container">
                    <div className="reports_container">
                        <div className="reports_section">
                            <h3>Eksport danych pomiarowych</h3>
                            <p>
                                Złóż żądanie raportu z danymi pomiarowymi dla wybranych liczników i
                                zakresu czasowego. Raport zostanie przygotowany i wysłany na adres
                                e-mail.
                            </p>
                        </div>

                        <div className="reports_form">
                            {/* Date Range Selection */}
                            <div className="form_group">
                                <SelectField
                                    label="Zakres czasowy raportu:"
                                    value={isCustomDateRange ? 'custom' : dateRange}
                                    onChange={(e) => handleDateRangeChange(e.target.value)}
                                >
                                    {dateRangeOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </SelectField>
                                <div className="date_range_display">{getDateRangeText()}</div>
                            </div>

                            {/* Meter Selection */}
                            <div className="form_group">
                                <div className="form_label">
                                    {isInstitutionalMonitoring
                                        ? 'Wybrane urządzenia:'
                                        : 'Wybrane liczniki:'}
                                </div>
                                <Button onClick={() => setIsMeterSelectDialogOpen(true)}>
                                    {getSelectedMetersText()}
                                </Button>
                            </div>

                            {/* Resolution Selection */}
                            <div className="form_group">
                                <SelectField
                                    label="Rozdzielczość danych:"
                                    value={resolution}
                                    onChange={(e) => dispatch(setResolution(e.target.value))}
                                >
                                    {resolutionOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </SelectField>
                            </div>

                            {/* Export Button */}
                            <div className="form_group">
                                <Button
                                    appearance="primary"
                                    isLoading={isExporting}
                                    onClick={handleExportClick}
                                    disabled={selectedMeterIds.length === 0 || !dateFrom || !dateTo}
                                >
                                    {isExporting ? 'Wysyłanie...' : 'Wygeneruj raport'}
                                </Button>
                                {exportError && (
                                    <div className="error_message">Błąd: {exportError}</div>
                                )}
                                {lastExportSuccess && (
                                    <div className="success_message">
                                        Wygenerowanie raportu zostało przyjęte i jest przetwarzane
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Date Range Dialog */}
            <Dialog
                isShown={isCustomDateRange}
                title="Wybierz zakres dat"
                onCloseComplete={() => setIsCustomDateRange(false)}
                confirmLabel="Zastosuj"
                onConfirm={handleCustomDateChange}
            >
                <div className="custom_date_form">
                    <div className="date_picker_group">
                        <div className="form_label">Data początkowa:</div>
                        <DatePicker
                            selected={customDateFrom}
                            onChange={setCustomDateFrom}
                            locale="pl"
                            dateFormat="dd.MM.yyyy"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            timeCaption="Godzina"
                        />
                    </div>
                    <div className="date_picker_group">
                        <div className="form_label">Data końcowa:</div>
                        <DatePicker
                            selected={customDateTo}
                            onChange={setCustomDateTo}
                            locale="pl"
                            dateFormat="dd.MM.yyyy"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            timeCaption="Godzina"
                        />
                    </div>
                </div>
            </Dialog>

            {/* Meter Selection Dialog */}
            <Dialog
                isShown={isMeterSelectDialogOpen}
                title={isInstitutionalMonitoring ? 'Wybierz urządzenia' : 'Wybierz liczniki'}
                onCloseComplete={() => {
                    setIsMeterSelectDialogOpen(false)
                    setSearchText('')
                    setMeterOffset(0)
                    setMeterPageCount(1)
                }}
                hasFooter={false}
                width={window.innerWidth < 768 ? '90vw' : '500px'}
            >
                <div className="meter_selection_dialog">
                    <div className="search_section">
                        <TextInputField
                            label="Szukaj"
                            placeholder={
                                isInstitutionalMonitoring
                                    ? 'Szukaj urządzenia...'
                                    : 'Szukaj licznika...'
                            }
                            value={searchText}
                            onChange={(e) => handleMeterSearch(e.target.value)}
                        />
                    </div>

                    <div className="meters_list">
                        {getFilteredMeters().map((meter) => (
                            <div key={meter.pk} className="meter_item">
                                <Checkbox
                                    label={renderMeterLabel(meter)}
                                    checked={selectedMeterIds.includes(meter.pk)}
                                    onChange={() => handleMeterToggle(meter.pk)}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="pagination_section">
                        <div className="pagination_controls">
                            <button
                                type="button"
                                onClick={() => handleMeterPageClick(-1)}
                                disabled={meterPageCount === 1}
                            >
                                ←
                            </button>
                            <span className="pagination_info">
                                Strona {meterPageCount} z{' '}
                                {Math.ceil((fetchMeters.data?.count || 0) / 20) || 1}
                            </span>
                            <button
                                type="button"
                                onClick={() => handleMeterPageClick(1)}
                                disabled={
                                    meterPageCount >= Math.ceil((fetchMeters.data?.count || 0) / 20)
                                }
                            >
                                →
                            </button>
                        </div>
                    </div>

                    <div className="dialog_footer">
                        <div className="selected_count">Wybrano: {selectedMeterIds.length}</div>
                        <Button onClick={() => setIsMeterSelectDialogOpen(false)}>Zamknij</Button>
                    </div>
                </div>
            </Dialog>

            <Footer />
        </div>
    )
}

export default ReportsModerator
